/**
 * A project's identity: a colour and a glyph the creator picks.
 *
 * A list of projects is a wall of near-identical cards — same layout, same
 * words, differing only in a title people skim past. Outlook solved this for
 * calendars years ago with a colour per entry, and it works because colour is
 * recognised before text is read. The glyph does the same job at a glance:
 * dumbbells for the gym project, a tent for camp.
 *
 * Colour is stored as DATA (a palette name, or a custom hex from the picker) and
 * resolved to paint here, so no component hardcodes a colour and a tenant's
 * brand still governs everything else on the page.
 */

export const PROJECT_COLORS = [
  'indigo', 'blue', 'teal', 'green', 'amber', 'orange', 'rose', 'purple', 'slate',
] as const;

export type ProjectColor = typeof PROJECT_COLORS[number];

/**
 * Resolve a stored colour to a CSS value.
 *
 * A palette name becomes the module's own token; anything starting with `#` is
 * a custom colour the creator picked and is passed through. Unset falls back to
 * the tenant's brand accent, so a project nobody styled still looks like it
 * belongs to this install rather than defaulting to some arbitrary blue.
 */
export function resolveProjectColor(value?: string | null): string {
  if (!value) return 'var(--primary)';
  if (value.startsWith('#')) return value;
  return PROJECT_COLORS.includes(value as ProjectColor)
    ? `var(--project-${value})`
    : 'var(--primary)';
}

/** The glyphs on offer. Deliberately a small, concrete set — a picker with a
 *  hundred icons is a picker nobody finishes. */
export const PROJECT_ICONS = [
  'board', 'camp', 'gym', 'calendar', 'rocket', 'chart',
  'book', 'people', 'idea', 'flag', 'heart', 'globe',
] as const;

export type ProjectIconName = typeof PROJECT_ICONS[number];

const PATHS: Record<string, JSX.Element> = {
  board: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <path d="M3 8.5h18M7 12.5h4.5M7 16h3M15 12.5h2M15 16h2" />
    </>
  ),
  camp: (
    <>
      <path d="M12 3 3 20h18L12 3Z" />
      <path d="M12 9v11" />
    </>
  ),
  gym: (
    <>
      <path d="M4 9v6M20 9v6M7 7v10M17 7v10" />
      <path d="M7 12h10" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  rocket: (
    <>
      <path d="M12 3c3.5 2.2 5.5 5.6 5.5 9.3L12 17l-5.5-4.7C6.5 8.6 8.5 5.2 12 3Z" />
      <path d="M9 17c-1 1.6-1.2 3-1 4 1-.2 2.4-.6 3.5-1.8M15 17c1 1.6 1.2 3 1 4-1-.2-2.4-.6-3.5-1.8" />
      <circle cx="12" cy="10" r="1.6" />
    </>
  ),
  chart: (
    <>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </>
  ),
  book: (
    <>
      <path d="M4 5a2 2 0 0 1 2-2h12v18H6a2 2 0 0 1-2-2Z" />
      <path d="M8 3v18" />
    </>
  ),
  people: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16 5.4a3.2 3.2 0 0 1 0 5.2M17.5 14.2A6.5 6.5 0 0 1 21.5 20" />
    </>
  ),
  idea: (
    <>
      <path d="M9 18h6M10 21h4" />
      <path d="M12 3a6 6 0 0 0-3.5 10.9c.5.4.8 1 .9 1.6h5.2c.1-.6.4-1.2.9-1.6A6 6 0 0 0 12 3Z" />
    </>
  ),
  flag: (
    <>
      <path d="M5 21V4M5 4h11l-2 3.5L16 11H5" />
    </>
  ),
  heart: (
    <>
      <path d="M12 20s-7-4.4-7-9a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 4.6-7 9-7 9Z" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.6 2.5 15.4 0 18-2.5-2.6-2.5-15.4 0-18Z" />
    </>
  ),
};

export interface ProjectGlyphProps {
  icon?: string | null;
  strokeWidth?: number;
  className?: string;
}

/** The chosen glyph, falling back to the board mark. */
export function ProjectGlyph({ icon, strokeWidth = 1.8, className }: ProjectGlyphProps) {
  const paths = PATHS[icon || 'board'] || PATHS.board;
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths}
    </svg>
  );
}

export default ProjectGlyph;
